import { LogOut, ChevronDown, Search, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '../../stores/auth.store';
import adminService from '../../services/admin.service';
import type { Customer } from '../../types';

/**
 * Top bar: which customer you are acting as, and who you are.
 *
 * Design notes (see docs/DESIGN-SYSTEM.md):
 *  - The customer switcher is the most consequential control in the portal, since it
 *    changes whose data you see. It is therefore labelled, not just a bare name.
 *  - Menus close on Escape and on outside click, and the trigger reports its state to
 *    assistive technology.
 *  - The selected customer is marked with a check as well as colour.
 */
export function Header() {
  const { user, customer, customers, selectCustomer, logout } = useAuthStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCustomerMenu, setShowCustomerMenu] = useState(false);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const customerMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isGlobalAdmin = user?.roles?.includes('GlobalAdmin');

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (customerMenuRef.current && !customerMenuRef.current.contains(event.target as Node)) {
        setShowCustomerMenu(false);
        setCustomerSearch('');
      }
    }

    // Escape should dismiss an open menu; a menu you cannot close with the keyboard
    // is a trap for keyboard users.
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        setShowCustomerMenu(false);
        setCustomerSearch('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // GlobalAdmin can act on any customer, so the full list is fetched lazily the first
  // time the menu opens rather than on every page load.
  //
  // The loading flag is set inside the async callback rather than synchronously in the
  // effect body, which avoids a cascading render on open.
  useEffect(() => {
    if (!showCustomerMenu) return;

    let cancelled = false;

    if (isGlobalAdmin && allCustomers.length === 0) {
      void (async () => {
        setLoadingCustomers(true);
        try {
          const data = await adminService.getCustomers(undefined, 0, 100);
          if (!cancelled) setAllCustomers(data.customers);
        } catch {
          // Leave the list empty; the menu shows its own "no customers" state.
        } finally {
          if (!cancelled) setLoadingCustomers(false);
        }
      })();
    }

    const focusTimer = setTimeout(() => searchInputRef.current?.focus(), 50);

    return () => {
      cancelled = true;
      clearTimeout(focusTimer);
    };
  }, [showCustomerMenu, isGlobalAdmin, allCustomers.length]);

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || '??'
    : '??';

  const displayCustomers = isGlobalAdmin ? allCustomers : customers;
  const canSwitch = isGlobalAdmin || customers.length > 1;

  const filteredCustomers = customerSearch
    ? displayCustomers.filter((c) => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
    : displayCustomers;

  const handleSelectCustomer = (c: Customer) => {
    selectCustomer(c);
    setShowCustomerMenu(false);
    setCustomerSearch('');
  };

  const customerLabel = customer
    ? `${customer.customerNumber ? `#${customer.customerNumber} ` : ''}${customer.name}`
    : 'Velg kunde';

  return (
    <header className="h-14 bg-white border-b border-ink-200/70 flex items-center justify-between px-5 shrink-0">
      {/* Customer context */}
      <div className="relative" ref={customerMenuRef}>
        <span className="block text-[10px] font-medium text-ink-400 uppercase tracking-[0.1em] leading-none mb-0.5">
          Kunde
        </span>

        {canSwitch ? (
          <button
            type="button"
            onClick={() => setShowCustomerMenu((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={showCustomerMenu}
            className="group flex items-center gap-1.5 -ml-1 px-1 py-0.5 rounded-[--radius-control] hover:bg-ink-100 transition-colors"
          >
            <span className="text-[13px] font-semibold text-ink-900">{customerLabel}</span>
            <ChevronDown
              size={13}
              className={`text-ink-400 transition-transform duration-150 ${
                showCustomerMenu ? 'rotate-180' : ''
              }`}
            />
          </button>
        ) : (
          <span className="block text-[13px] font-semibold text-ink-900">{customerLabel}</span>
        )}

        {showCustomerMenu && canSwitch && (
          <div
            role="listbox"
            className="absolute top-full left-0 mt-2 w-80 bg-white rounded-[--radius-surface] shadow-[--shadow-overlay] border border-ink-200/70 py-1 z-50"
          >
            {isGlobalAdmin && (
              <div className="px-2 pb-2 pt-1 border-b border-ink-100">
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Søk kunder..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-[13px] bg-ink-50 border border-transparent rounded-[--radius-control] placeholder:text-ink-400 focus:bg-white focus:border-accent-300 focus:outline-none transition-colors"
                  />
                </div>
              </div>
            )}

            <div className="max-h-72 overflow-y-auto py-1">
              {loadingCustomers ? (
                <p className="px-3 py-6 text-[13px] text-ink-400 text-center">Laster kunder...</p>
              ) : filteredCustomers.length === 0 ? (
                <p className="px-3 py-6 text-[13px] text-ink-400 text-center">
                  Ingen kunder funnet
                </p>
              ) : (
                filteredCustomers.map((c) => {
                  const isSelected = c.id === customer?.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelectCustomer(c)}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 text-[13px] transition-colors ${
                        isSelected
                          ? 'bg-accent-50 text-accent-800 font-medium'
                          : 'text-ink-700 hover:bg-ink-50'
                      }`}
                    >
                      {/* Reserve the check column so rows do not shift on selection. */}
                      <Check
                        size={14}
                        className={isSelected ? 'text-accent-600 shrink-0' : 'opacity-0 shrink-0'}
                      />
                      {c.customerNumber && (
                        <span className="text-ink-400 tabular-nums">#{c.customerNumber}</span>
                      )}
                      <span className="truncate">{c.name}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Account */}
      <div className="relative" ref={userMenuRef}>
        <button
          type="button"
          onClick={() => setShowUserMenu((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={showUserMenu}
          className="flex items-center gap-2.5 pl-1.5 pr-2 py-1 rounded-[--radius-control] hover:bg-ink-100 transition-colors"
        >
          <span className="w-7 h-7 bg-ink-900 rounded-full flex items-center justify-center text-white text-[11px] font-semibold shrink-0">
            {initials}
          </span>
          <span className="text-left hidden sm:block leading-tight">
            <span className="block text-[13px] font-medium text-ink-900">
              {user?.firstName} {user?.lastName}
            </span>
            <span className="block text-[11px] text-ink-500">{user?.email}</span>
          </span>
          <ChevronDown
            size={13}
            className={`text-ink-400 transition-transform duration-150 ${
              showUserMenu ? 'rotate-180' : ''
            }`}
          />
        </button>

        {showUserMenu && (
          <div
            role="menu"
            className="absolute top-full right-0 mt-2 w-56 bg-white rounded-[--radius-surface] shadow-[--shadow-overlay] border border-ink-200/70 py-1 z-50"
          >
            <div className="px-3 py-2 border-b border-ink-100 sm:hidden">
              <p className="text-[13px] font-medium text-ink-900">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-[11px] text-ink-500 truncate">{user?.email}</p>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={logout}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] text-ink-700 hover:bg-ink-50 transition-colors"
            >
              <LogOut size={15} className="text-ink-400" />
              <span>Logg ut</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
